import { Injectable, inject } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { AppService, APIStates } from 'src/services/app.service';
import { FrontendPermissions } from '@vierkant-software/types__api';

/**
 * Guard to ensure checkout app is properly initialized before allowing navigation
 * 
 * Route data configuration:
 * - allowWhenNotConfigured: boolean - Allow access even when checkout is not configured
 * - requiresLogin: boolean - Require full login (default: true)
 * - permissions: FrontendPermissions[] - Required permissions for access
 */
@Injectable({ providedIn: 'root' })
export class CashRegisterActivateGuard implements CanActivate {
  private appService = inject(AppService);
  private router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.checkAccess(route);
  }

  private checkAccess(route: ActivatedRouteSnapshot): boolean {
    const apiState = this.appService.state;
    const allowWhenNotConfigured = route.data?.allowWhenNotConfigured === true;
    const requiresLogin = route.data?.requiresLogin !== false;
    const permissions: FrontendPermissions[] = route.data?.permissions || [];

    // Allow navigation to routes that are accessible when not configured
    if (allowWhenNotConfigured && (apiState === APIStates.disconnected || apiState === APIStates.loggedOut))
      return true;

    // Allow access to routes that don't require login when connected
    if (!requiresLogin && apiState === APIStates.connected)
      return true;

    // Check if logged in
    if (apiState === APIStates.loggedIn) {
      // Check required permissions
      if (permissions.length > 0 && !this.appService.getFrontendPermissions(permissions)) {
        console.warn('[CashRegisterActivateGuard] Missing required permissions:', permissions.map(p => FrontendPermissions[p]).join(', '));
        return false;
      }
      return true;
    }

    // Redirect based on current state
    switch(apiState) {
      case APIStates.disconnected:
      case APIStates.loggedOut:
        this.router.navigate(['/config']).catch(console.error);
        return false;
      case APIStates.connected:
        this.router.navigate(['/login']).catch(console.error);
        return false;
      default:
        // Still initializing
        return false;
    }
  }
}
